import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Typo } from '@/components/ui/typography';
import { VoucherInput } from '@/components/web/cart/voucher-input';
import { UserCartItem, useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { formatCurrency } from '@/lib/accounting';
import { formatDate } from '@/lib/date';
import { cn } from '@/lib/utils';
import { type SharedData } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import { isPast } from 'date-fns';
import { MapPin, ShoppingCart } from 'lucide-react';
import { useMemo } from 'react';
import { SummaryVoucher } from '../web/cart/summary-voucher';

interface OrderSummaryCardProps {
  selectedItems: UserCartItem[];
  total: number;
  onContinue?: () => void;
  continueText?: string;
  continueDisabled?: boolean;
  showContinueButton?: boolean;
  isLoading?: boolean;
  className?: string;
  step?: 'cart' | 'checkout';
  discount?: number;
}

export function OrderSummaryCard({
  selectedItems,
  total,
  onContinue,
  continueText = 'Tiếp tục',
  continueDisabled = false,
  showContinueButton = true,
  isLoading = false,
  className = '',
  step = 'cart',
  discount = 0,
}: OrderSummaryCardProps) {
  const { appliedVoucher } = usePage<SharedData>().props;
  const { selectedSessions } = useUserGlobalCart();
  const formatCartItemDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return formatDate(dateString);
    } catch {
      return dateString;
    }
  };

  const groupedItems = selectedItems.reduce(
    (acc, item) => {
      const siteKey = item.options?.site_mapping_code || 'unknown';
      const date = item.options?.date || '';
      const key = `${siteKey}_${date}`;

      if (!acc[key]) {
        acc[key] = {
          siteName: item.options?.site_name || 'Địa điểm không xác định',
          siteCode: siteKey,
          date: date,
          items: [],
        };
      }
      acc[key].items.push(item);
      return acc;
    },
    {} as Record<string, { siteName: string; siteCode: string; date: string; items: UserCartItem[] }>,
  );

  const isNeedLockSlot = useMemo(() => {
    if (selectedItems.length === 0) return false;
    const hasInventories = selectedItems.filter((item) => item.options.has_inventory);
    return (
      hasInventories &&
      selectedItems.some(
        (item) =>
          (item.options.has_inventory && !item.options.reservation_code) ||
          (item.options.has_inventory && item.options.reservation_code && item.options.expired_at && !isPast(item.options.expired_at)),
      )
    );
  }, [selectedItems]);

  if (selectedItems.length === 0) {
    return (
      <Card className={cn('sticky top-24 h-fit', className)}>
        <CardContent className="p-6 text-center">
          <ShoppingCart className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <Typo variant="h5" className="mb-2 font-medium text-gray-900">
            Chưa chọn dịch vụ
          </Typo>
          <Typo variant="muted">{step === 'cart' ? 'Chọn dịch vụ từ danh sách để tiếp tục' : 'Quay lại trang giỏ hàng để chọn dịch vụ'}</Typo>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('sticky top-24 h-fit', className)}>
      <CardContent className="space-y-4 p-6">
        {/* Selected Items by Site and Date */}
        <div className="space-y-4">
          {Object.values(groupedItems).map((group, groupIndex) => (
            <div key={groupIndex} className="space-y-3">
              {/* Site and Date Header */}
              <div className="rounded-lg">
                <div className="mb-1 flex items-center gap-2">
                  <MapPin className="text-primary size-4" />
                  <Typo variant="h5" className="font-medium">
                    {group.siteName} - {formatCartItemDate(group.date)}
                  </Typo>
                </div>
                {/* <div className="flex items-center gap-2">
                  <Calendar className="size-4 text-primary" />
                  <Typo variant="small" className="text-xs">
                    {formatCartItemDate(group.date)}
                  </Typo>
                </div> */}
              </div>
              <Separator />

              {/* Items in this group */}
              <div className="space-y-2">
                {group.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <div className="flex-1">
                      <Typo variant="small" className="font-medium text-gray-900">
                        {item.name}
                      </Typo>
                      <Typo variant="muted" className="text-xs">
                        {item.options?.type_name && `${item.options.type_name} • `}
                        Số lượng: {item.qty}
                      </Typo>
                      {item.options.has_inventory && selectedSessions[item.rowId] && (
                        <Typo variant="muted" className="text-xs">
                          {selectedSessions[item.rowId].outlet_name}, {selectedSessions[item.rowId].start_time}
                        </Typo>
                      )}
                    </div>
                    <div className="text-right">
                      <Typo variant="h6" className="text-primary">
                        {formatCurrency(item.total)}
                      </Typo>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {/* Voucher renders on both steps: "Mua ngay" skips the cart page entirely. */}
        {!appliedVoucher ? (
          <>
            <Separator />
            <VoucherInput selectedItems={selectedItems} />
          </>
        ) : (
          <>
            <Separator />
            <SummaryVoucher voucher={appliedVoucher} selectedItems={selectedItems} />
          </>
        )}
        <Separator />
        {/* Total Section */}
        <div className="space-y-2">
          {discount > 0 && (
            <>
              <div className="flex items-center justify-between text-lg">
                <Typo variant="muted">Giảm giá:</Typo>
                <Typo variant="h6" className="text-blue-500">
                  {formatCurrency(discount)}
                </Typo>
              </div>
            </>
          )}
          <div className="flex items-center justify-between text-lg font-semibold">
            <Typo variant="muted">Tổng cộng:</Typo>
            <Typo variant="h5" className="text-primary">
              {formatCurrency(total)}
            </Typo>
          </div>
        </div>
        {/* Action Button */}
        {showContinueButton && (
          <div className="flex flex-col gap-2 pt-4">
            <Button onClick={onContinue} disabled={continueDisabled || isLoading} className="w-full rounded-full" size="lg">
              {isLoading ? 'Đang xử lý...' : continueText}
            </Button>

            {step === 'checkout' && (
              <Button variant="outline" className="rounded-full" size="lg">
                <Link href="/cart">
                  {/* <ShoppingCart className="size-4" /> */}
                  Quay lại giỏ hàng
                </Link>
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
